import CloseIcon from '@mui/icons-material/Close'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { skipToken } from '@reduxjs/toolkit/query'
import {
  PortalDocumentRef,
  useGetPortalDocumentPresignedUrlMutation,
  useGetPortalOrderDocumentsQuery,
} from 'store/customer-portal/customer-portal-runtime.api'
import { ACCENT_BLUE } from 'utils/brand-colors'

interface Props {
  orderId: number | null
  orderNumber?: string
  onClose: () => void
}

// Visual structure mirrors the operator's invoice-documents-dialog table
// (Documents Type / Documents) but is read-only and customer-scoped.
// LC, Waybill, BOL, POD are returned for customers; operator-only types are filtered server-side.
const DOC_TYPE_LABELS: Record<keyof NonNullable<ReturnType<typeof useGetPortalOrderDocumentsQuery>['data']>, string> = {
  LC: 'Load Confirmation',
  WAYBILL: 'Way bill',
  BOL: 'BOL',
  POD: 'POD',
}

const PortalDocumentsDialog = ({ orderId, orderNumber, onClose }: Props) => {
  const { data, isFetching } = useGetPortalOrderDocumentsQuery(
    orderId ? { orderId } : skipToken
  )
  const [getPresignedUrl] = useGetPortalDocumentPresignedUrlMutation()

  const handleOpen = async (doc: PortalDocumentRef) => {
    try {
      const res = await getPresignedUrl({
        s3URL: doc.s3Path,
        openInline: true,
      }).unwrap()
      window.open(res.preSignedURL, '_blank', 'noopener,noreferrer')
    } catch (e) {
      console.error('Failed to open document', e)
    }
  }

  const sections: { key: 'LC' | 'WAYBILL' | 'BOL' | 'POD'; label: string }[] = [
    { key: 'LC', label: DOC_TYPE_LABELS.LC },
    { key: 'WAYBILL', label: DOC_TYPE_LABELS.WAYBILL },
    { key: 'BOL', label: DOC_TYPE_LABELS.BOL },
    { key: 'POD', label: DOC_TYPE_LABELS.POD },
  ]

  return (
    <Dialog
      open={!!orderId}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '2px',
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.12)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.25,
          bgcolor: 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
              Documents
            </Typography>
            {orderNumber ? (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, fontWeight: 500 }}>
                Order {orderNumber}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            edge="end"
            size="small"
            sx={{
              color: 'text.secondary',
              mt: -0.5,
              '&:hover': { bgcolor: alpha(ACCENT_BLUE, 0.08), color: 'primary.main' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 0, py: 0, bgcolor: 'common.white' }}>
        {isFetching ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 0,
              border: 'none',
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: alpha(ACCENT_BLUE, 0.04),
                    '& th': { borderBottom: '1px solid', borderColor: 'divider' },
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'text.secondary',
                      textTransform: 'none',
                      letterSpacing: '0',
                      py: 1.75,
                      width: 200,
                    }}
                  >
                    Document Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'text.secondary',
                      textTransform: 'none',
                      letterSpacing: '0',
                      py: 1.75,
                    }}
                  >
                    Documents
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sections.map(({ key, label }) => {
                  const docs = data?.[key] ?? []
                  return (
                    <TableRow
                      key={key}
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        '& td': { borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top' },
                      }}
                    >
                      <TableCell sx={{ py: 2, verticalAlign: 'top' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', lineHeight: 1.43 }}>
                          {label}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {docs.length === 0 ? (
                          <Typography sx={{ color: 'text.secondary', fontSize: 14, fontStyle: 'italic' }}>
                            No documents uploaded
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {docs.map((d) => (
                              <Paper
                                key={d.s3Path}
                                variant="outlined"
                                role="button"
                                tabIndex={0}
                                onClick={() => handleOpen(d)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleOpen(d)
                                  }
                                }}
                                sx={{
                                  p: 1.25,
                                  pl: 1.5,
                                  borderRadius: '2px',
                                  cursor: 'pointer',
                                  borderColor: alpha(ACCENT_BLUE, 0.15),
                                  bgcolor: 'common.white',
                                  transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
                                  '&:hover': {
                                    borderColor: alpha(ACCENT_BLUE, 0.45),
                                    bgcolor: alpha(ACCENT_BLUE, 0.05),
                                    boxShadow: `0 1px 6px ${alpha(ACCENT_BLUE, 0.12)}`,
                                  },
                                  '&:focus-visible': {
                                    outline: `2px solid ${alpha(ACCENT_BLUE, 0.55)}`,
                                    outlineOffset: 1,
                                  },
                                }}
                              >
                                <Stack direction="row" alignItems="center" spacing={1.25}>
                                  <DescriptionOutlinedIcon sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 500,
                                      color: 'primary.main',
                                      textAlign: 'left',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {d.originalName || d.documentName}
                                  </Typography>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PortalDocumentsDialog
